import { BrandLogo } from '../components/Brand';
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { statsAPI } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { Badge } from "../components/ui/badge";
import { 
    Scale, 
    Trophy, 
    Target, 
    Zap, 
    BookOpen, 
    BarChart3,
    Award,
    Flame,
    ArrowLeft,
    LogOut,
    TrendingUp,
    Crown,
    User,
    Calendar,
    Percent
} from "lucide-react";

const ACHIEVEMENTS = {
    "first_game": { name: "First Steps", description: "Complete your first game", icon: Trophy, color: "text-violet-400", xp: 50 },
    "perfect_round": { name: "Perfect Round", description: "Get all questions correct", icon: Crown, color: "text-yellow-400", xp: 100 },
    "streak_5": { name: "On Fire", description: "5 correct in a row", icon: Flame, color: "text-orange-400", xp: 75 },
    "streak_10": { name: "Unstoppable", description: "10 correct in a row", icon: Zap, color: "text-cyan-400", xp: 150 },
    "games_10": { name: "Dedicated", description: "Play 10 games", icon: Target, color: "text-emerald-400", xp: 100 },
    "games_50": { name: "Committed", description: "Play 50 games", icon: Award, color: "text-pink-400", xp: 250 },
    "score_1000": { name: "Rising Star", description: "Reach 1000 points", icon: TrendingUp, color: "text-blue-400", xp: 100 },
    "score_5000": { name: "Legal Eagle", description: "Reach 5000 points", icon: Crown, color: "text-amber-400", xp: 300 },
    "all_subjects": { name: "Well Rounded", description: "Play all 8 subjects", icon: BookOpen, color: "text-purple-400", xp: 200 },
    "speed_demon": { name: "Speed Demon", description: "Answer in under 5 sec", icon: Zap, color: "text-red-400", xp: 75 },
};

export default function ProfilePage() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    
    useEffect(() => {
        loadStats();
    }, []);
    
    const loadStats = async () => {
        try {
            const response = await statsAPI.getMyStats();
            setStats(response.data);
        } catch (error) {
            console.error("Failed to load stats:", error);
        } finally {
            setLoading(false);
        }
    };
    
    const handleLogout = () => {
        logout();
        navigate("/");
    };
    
    const xpProgress = stats ? ((stats.xp % 500) / 500) * 100 : 0;
    
    return (
        <div className="min-h-screen bg-background">
            {/* Navigation */}
            <nav className="sticky top-0 z-50 glass border-b border-slate-800 px-6 py-4">
                <div className="max-w-4xl mx-auto flex items-center justify-between">
                    <Link to="/dashboard" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
                        <ArrowLeft className="w-5 h-5" />
                        <span className="hidden sm:inline">Back to Dashboard</span>
                    </Link>
                    
                    <BrandLogo />
                    
                    <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={handleLogout}
                        className="text-slate-400 hover:text-white"
                        data-testid="profile-logout-btn"
                    >
                        <LogOut className="w-4 h-4" />
                    </Button>
                </div>
            </nav>
            
            <main className="max-w-4xl mx-auto px-6 py-8">
                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                    </div>
                ) : stats ? (
                    <>
                        {/* Profile Header */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="text-center mb-8"
                        >
                            <div className="relative inline-block mb-4">
                                <div className="w-24 h-24 rounded-full bg-primary/20 border-4 border-primary flex items-center justify-center">
                                    <User className="w-12 h-12 text-primary" />
                                </div>
                                <div className="absolute -bottom-2 -right-2 w-10 h-10 rounded-full bg-slate-900 border-2 border-primary flex items-center justify-center">
                                    <span className="font-heading font-bold text-primary">{stats.level}</span>
                                </div>
                            </div>
                            <h1 className="font-heading text-3xl font-bold text-white mb-1">
                                {user?.username}
                            </h1>
                            <p className="text-slate-400">{user?.email}</p>
                            
                            {/* XP Bar */}
                            <div className="max-w-xs mx-auto mt-4">
                                <div className="flex justify-between text-xs text-slate-400 mb-1">
                                    <span>Level {stats.level}</span>
                                    <span>{stats.xp % 500} / 500 XP</span>
                                </div>
                                <Progress value={xpProgress} className="h-2 bg-slate-800" />
                            </div>
                        </motion.div>
                        
                        {/* Stats Grid */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 }}
                            className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8"
                        >
                            <Card className="glass border-slate-800 rounded-xl" data-testid="stat-score">
                                <CardContent className="p-4 text-center">
                                    <Trophy className="w-6 h-6 text-amber-400 mx-auto mb-2" />
                                    <p className="font-heading text-2xl font-bold text-white">
                                        {stats.total_score.toLocaleString()}
                                    </p>
                                    <p className="text-slate-400 text-xs">Total Score</p>
                                </CardContent>
                            </Card>
                            
                            <Card className="glass border-slate-800 rounded-xl" data-testid="stat-games">
                                <CardContent className="p-4 text-center">
                                    <Target className="w-6 h-6 text-cyan-400 mx-auto mb-2" />
                                    <p className="font-heading text-2xl font-bold text-white">
                                        {stats.games_played}
                                    </p>
                                    <p className="text-slate-400 text-xs">Games Played</p>
                                </CardContent>
                            </Card>
                            
                            <Card className="glass border-slate-800 rounded-xl" data-testid="stat-accuracy">
                                <CardContent className="p-4 text-center">
                                    <Percent className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
                                    <p className="font-heading text-2xl font-bold text-emerald-400">
                                        {stats.accuracy}%
                                    </p>
                                    <p className="text-slate-400 text-xs">Accuracy</p>
                                </CardContent>
                            </Card>
                            
                            <Card className="glass border-slate-800 rounded-xl" data-testid="stat-streak">
                                <CardContent className="p-4 text-center">
                                    <Flame className="w-6 h-6 text-orange-400 mx-auto mb-2" />
                                    <p className="font-heading text-2xl font-bold text-orange-400">
                                        {stats.best_streak}
                                    </p>
                                    <p className="text-slate-400 text-xs">Best Streak</p>
                                </CardContent>
                            </Card>
                        </motion.div>
                        
                        {/* Achievements */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 }}
                            className="mb-8"
                        >
                            <h2 className="font-heading text-xl font-bold text-white mb-4 flex items-center gap-2">
                                <Award className="w-5 h-5 text-amber-400" />
                                Achievements ({stats.achievements.length}/{Object.keys(ACHIEVEMENTS).length})
                            </h2>
                            
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="achievements-grid">
                                {Object.entries(ACHIEVEMENTS).map(([id, achievement]) => {
                                    const isUnlocked = stats.achievements.includes(id);
                                    const Icon = achievement.icon;
                                    
                                    return (
                                        <Card 
                                            key={id}
                                            className={`rounded-xl ${
                                                isUnlocked 
                                                    ? "glass border-slate-700" 
                                                    : "bg-slate-900/30 border-slate-800/50"
                                            }`}
                                        >
                                            <CardContent className="p-4 flex items-center gap-4">
                                                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                                                    isUnlocked 
                                                        ? "bg-slate-800" 
                                                        : "bg-slate-800/50"
                                                }`}>
                                                    <Icon className={`w-6 h-6 ${
                                                        isUnlocked ? achievement.color : "text-slate-600"
                                                    }`} />
                                                </div>
                                                <div className="flex-1">
                                                    <p className={`font-semibold ${
                                                        isUnlocked ? "text-white" : "text-slate-500"
                                                    }`}>
                                                        {achievement.name}
                                                    </p>
                                                    <p className={`text-xs ${
                                                        isUnlocked ? "text-slate-400" : "text-slate-600"
                                                    }`}>
                                                        {achievement.description}
                                                    </p>
                                                </div>
                                                <Badge className={`${
                                                    isUnlocked 
                                                        ? "bg-primary/20 text-primary" 
                                                        : "bg-slate-800 text-slate-500"
                                                } border-0 text-xs`}>
                                                    +{achievement.xp} XP
                                                </Badge>
                                            </CardContent>
                                        </Card>
                                    );
                                })}
                            </div>
                        </motion.div>
                        
                        {/* Subject Progress */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3 }}
                        >
                            <h2 className="font-heading text-xl font-bold text-white mb-4 flex items-center gap-2">
                                <BookOpen className="w-5 h-5 text-emerald-400" />
                                Subject Progress ({stats.subjects_played.length}/8)
                            </h2>
                            
                            <Card className="glass border-slate-800 rounded-2xl" data-testid="subjects-progress">
                                <CardContent className="p-6">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        {Object.entries(stats.subject_stats).map(([subject, data]) => (
                                            <div 
                                                key={subject}
                                                className={`p-4 rounded-xl ${
                                                    data.played 
                                                        ? "bg-emerald-500/10 border border-emerald-500/30" 
                                                        : "bg-slate-800/40 border border-slate-700/50"
                                                }`}
                                            >
                                                <div className="flex items-center justify-between mb-2">
                                                    <span className={`font-medium text-sm ${
                                                        data.played ? "text-emerald-400" : "text-slate-500"
                                                    }`}>
                                                        {subject}
                                                    </span>
                                                    {data.played && (
                                                        <Badge className="bg-emerald-500/20 text-emerald-400 border-0 text-xs">
                                                            {data.mastery}%
                                                        </Badge>
                                                    )}
                                                </div>
                                                <Progress 
                                                    value={data.played ? data.mastery : 0} 
                                                    className="h-1.5 bg-slate-700"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        </motion.div>
                    </>
                ) : null}
            </main>
        </div>
    );
}
